/* global runtime */
// Ansattportal, ansatte, faste lønnslinjer og lønnsinnstillinger.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.
const uploadEmployeePortalAttachment = async ({ businessId, employeeId, file } = {}) => {
  await initFirebase();
  if (!runtime.storage) throw new Error(t('svc.noStorage'));
  if (!businessId) throw new Error(t('svc.missingBusinessForAttachment'));
  if (!employeeId) throw new Error(t('svc.missingEmployeeForAttachment'));
  if (!file) throw new Error(t('svc.missingAttachment'));
  const id = createId('employee-portal-attachment');
  const storagePath = `employeePortal/${businessId}/${employeeId}/${id}`;
  const ref = runtime.storage.ref().child(storagePath);
  await ref.put(file, {
    contentType: file.type || 'application/octet-stream',
    contentDisposition: `inline; filename*=UTF-8''${encodeURIComponent(file.name || 'bilag')}`,
    customMetadata: { originalName: file.name || 'bilag' },
  });
  const downloadUrl = await ref.getDownloadURL().catch(() => '');
  return {
    id,
    name: file.name || t('pay.voucher'),
    mimeType: file.type || 'application/octet-stream',
    storagePath,
    downloadUrl: downloadUrl || undefined,
  };
};
const getEmployeePortalAttachmentDownloadUrl = async (storagePath = '') => {
  await initFirebase();
  const path = String(storagePath || '').trim();
  if (!path) return '';
  if (!runtime.storage) throw new Error(t('svc.noStorage'));
  return runtime.storage.ref().child(path).getDownloadURL();
};

// Svaret er enten en utleggslinje (privat, gaar til lonn) eller en innboksrad
// (betalt av foretaket, gaar til bokforing). Begge er en gyldig kvittering.
const submitEmployeePortalExpense = async (payload = {}) => {
  const result = await authorizedPost('submitEmployeeExpenseHttp', payload);
  return result.expense || result.receipt || null;
};

// Timeforing fra ansattportalen. Kundeappen skriver timer rett i app-state, men
// en ansatt er ikke medlem og ma gjennom serveren, som ogsa handhever periodelasen.
const mutateEmployeePortalTimeEntry = async (payload = {}) => {
  const result = await authorizedPost('mutateEmployeeTimeEntryHttp', payload);
  return result.deleted ? { deleted: true } : (result.timeEntry || null);
};

const submitEmployeePortalTravel = async (payload = {}) => {
  const result = await authorizedPost('submitEmployeeTravelHttp', payload);
  return result.travel || null;
};

// Ansatte er ikke medlemmer av virksomheten og kan ikke skrive app/state direkte.
// Utkast, innsending, tilbaketrekking og sletting fra ansattportalen gar derfor
// gjennom denne funksjonen, som sjekker eierskap og status pa serveren.
const mutateEmployeePortalTravel = async (payload = {}) => {
  const result = await authorizedPost('updateEmployeeTravelHttp', payload);
  return result.deleted ? { deleted: true } : (result.travel || null);
};

const manageEmployeePortalAccess = async ({ employeeId, mode = 'invite', businessId = runtime.activeBusinessId } = {}) => {
  if (!businessId) throw new Error(t('svc.missingActiveBusiness'));
  if (!employeeId) throw new Error(t('svc.missingEmployee'));
  const payload = await authorizedPost('manageEmployeePortalAccessHttp', {
    businessId,
    employeeId,
    mode,
  });
  const employee = payload.employee ? mapEmployeeToUi(payload.employee) : null;
  if (employee) {
    const existingEmployees = safeArray(runtime.appState?.employees).map(mapEmployeeToUi);
    const nextEmployees = existingEmployees.some((entry) => entry.id === employee.id)
      ? existingEmployees.map((entry) => entry.id === employee.id ? employee : entry)
      : [employee, ...existingEmployees];
    window.EMPLOYEES = nextEmployees;
    window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  }
  return { ...payload, employee };
};

const saveEmployee = async (employee = {}) => {
  const id = employee.id || employee._backend?.id || createId('emp');
  let savedEmployee = null;
  await updateStateAndPersist((state) => {
    const existing = safeArray(state.employees).find((entry) => entry.id === id);
    const backendEmployee = mapUiEmployeeToBackend({ ...employee, id }, existing);
    if (!backendEmployee.name) throw new Error(t('svc.nameMissing'));
    savedEmployee = stripUndefinedDeep({
      ...backendEmployee,
      updatedAt: nowIso(),
    });
    return {
      ...state,
      employees: existing
        ? safeArray(state.employees).map((entry) => entry.id === id ? savedEmployee : entry)
        : [savedEmployee, ...safeArray(state.employees)],
    };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return mapEmployeeToUi(savedEmployee);
};

const saveEmployeeFixedPayrollLine = async (employeeId, line = {}) => {
  if (!employeeId) throw new Error(t('svc.pickEmployee'));
  let savedEmployee = null;
  await updateStateAndPersist((state) => {
    const employees = safeArray(state.employees);
    const existingEmployee = employees.find((entry) => entry.id === employeeId);
    if (!existingEmployee) throw new Error(t('svc.employeeNotFound'));
    const existingLines = safeArray(existingEmployee.fixedPayrollLines);
    const existingLine = line.id ? existingLines.find((entry) => entry.id === line.id) : null;
    const payType = line.payType || existingLine?.payType || 'monthly-salary';
    const payTypeDefinition = getPayrollPayTypeDefinition(payType, state.payrollSettings);
    const preservesExistingType = existingLine && existingLine.payType === payType;
    if (!payTypeDefinition || ((!payTypeDefinition.active || payTypeDefinition.fixedLine === false) && !preservesExistingType)) {
      throw new Error(t('pay.payTypeNotFixedLine'));
    }
    const catalogRules = {
      payout: payTypeDefinition.kind !== 'Trekk' && payTypeDefinition.kind !== 'Naturalytelse',
      taxable: payTypeDefinition.tax !== false,
      employerContribution: payTypeDefinition.aga !== false,
      holidayPay: payTypeDefinition.holiday !== false,
      pension: payTypeDefinition.pension !== false,
    };
    const overrideReason = String(line.calculationRuleOverrideReason || '').trim();
    const overrideRules = line.calculationRuleOverride === true;
    if (overrideRules && !overrideReason) throw new Error(t('pay.explainRuleOverrideRequired'));
    const savedLine = normalizeFixedPayrollLine({
      ...line,
      ...(overrideRules ? {} : catalogRules),
      calculationRuleOverride: overrideRules || undefined,
      calculationRuleOverrideReason: overrideRules ? overrideReason : undefined,
    }, existingEmployee, existingLine || {});
    if (!savedLine.variableAmount && num(savedLine.amount) <= 0) {
      throw new Error(t('svc.amountAboveZero'));
    }
    savedEmployee = stripUndefinedDeep({
      ...existingEmployee,
      fixedPayrollLines: existingLine
        ? existingLines.map((entry) => entry.id === savedLine.id ? savedLine : entry)
        : [...existingLines, savedLine],
      updatedAt: nowIso(),
    });
    return {
      ...state,
      employees: employees.map((entry) => entry.id === employeeId ? savedEmployee : entry),
    };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return mapEmployeeToUi(savedEmployee);
};

const deleteEmployeeFixedPayrollLine = async (employeeId, lineId) => {
  if (!employeeId || !lineId) throw new Error(t('svc.missingFixedLine'));
  let savedEmployee = null;
  await updateStateAndPersist((state) => {
    const employees = safeArray(state.employees);
    const existingEmployee = employees.find((entry) => entry.id === employeeId);
    if (!existingEmployee) throw new Error(t('svc.employeeNotFound'));
    const nextLines = safeArray(existingEmployee.fixedPayrollLines).filter((line) => line.id !== lineId);
    if (nextLines.length === safeArray(existingEmployee.fixedPayrollLines).length) throw new Error(t('svc.fixedLineNotFound'));
    savedEmployee = stripUndefinedDeep({ ...existingEmployee, fixedPayrollLines: nextLines, updatedAt: nowIso() });
    return {
      ...state,
      employees: employees.map((entry) => entry.id === employeeId ? savedEmployee : entry),
    };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return mapEmployeeToUi(savedEmployee);
};

const savePayrollSettings = async (settings = {}) => {
  const savedAt = nowIso();
  const calculationCode = String(settings.calculationCode || 'general').trim().toLowerCase();
  if (calculationCode !== 'general') throw new Error(t('pay.calculationCodeUnsupportedShort'));
  await updateStateAndPersist((state) => {
    const currentSettings = state.payrollSettings || {};
    const previousHolidayPayPercent = num(currentSettings.holidayPayPercent, 10.2);
    const nextSettings = stripUndefinedDeep({
      ...(state.payrollSettings || {}),
      ...settings,
      calculationCode,
      holidayPayPercent: num(settings.holidayPayPercent ?? state.payrollSettings?.holidayPayPercent, 10.2),
      employerContributionRate: num(settings.employerContributionRate ?? state.payrollSettings?.employerContributionRate, 14.1),
      // The settings UI passes the company-rate mode explicitly. Keep the
      // lower-level service compatible with deliberate per-employee callers.
      useStandardEmployerContributionRate: settings.useStandardEmployerContributionRate
        ?? currentSettings.useStandardEmployerContributionRate
        ?? true,
      updatedAt: savedAt,
    });
    const employees = safeArray(state.employees).map((employee) => {
      const currentRate = Number(employee?.holidayPayRate);
      const followsStandard = employee?.useStandardHolidayPayRate === true || (
        employee?.useStandardHolidayPayRate !== false &&
        (!Number.isFinite(currentRate) || Math.abs(currentRate - previousHolidayPayPercent) <= 0.0001)
      );
      return followsStandard
        ? { ...employee, holidayPayRate: nextSettings.holidayPayPercent, useStandardHolidayPayRate: true }
        : { ...employee, useStandardHolidayPayRate: false };
    });
    const employeeById = new Map(employees.map((employee) => [String(employee.id || ''), employee]));
    const payrollRuns = safeArray(state.payrollRuns).map((run) => {
      if (run?.status !== 'draft') return run;
      const paymentDate = run.paymentDate || todayIso();
      const effectiveSettings = { ...nextSettings, country: state.settings?.country, effectiveDate: paymentDate, runTaxDeductionMode: normalizePayrollTaxDeductionMode(run.taxDeductionMode) };
      return stripUndefinedDeep({
        ...run,
        lines: safeArray(run.lines).map((line) => recalculatePayrollRunLine(
          employeeById.get(String(line.employeeId || '')) || line,
          line,
          paymentDate,
          effectiveSettings,
        )),
        updatedAt: savedAt,
      });
    });
    return { ...state, payrollSettings: nextSettings, employees, payrollRuns };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return mergeState(runtime.appState).payrollSettings;
};

const savePayrollPayType = async (payType = {}) => {
  let savedPayType = null;
  await updateStateAndPersist((state) => {
    const currentSettings = state.payrollSettings || {};
    const catalog = payrollPayTypesForSettings(currentSettings);
    const existing = catalog.find((entry) => entry.id === payType.id || entry.payType === payType.payType);
    savedPayType = normalizePayrollPayType(payType, existing || {});
    const customPayTypes = safeArray(currentSettings.customPayTypes);
    const nextCustomPayTypes = customPayTypes.some((entry) => entry.id === savedPayType.id || entry.payType === savedPayType.payType)
      ? customPayTypes.map((entry) => (entry.id === savedPayType.id || entry.payType === savedPayType.payType ? savedPayType : entry))
      : [...customPayTypes, savedPayType];
    return {
      ...state,
      payrollSettings: stripUndefinedDeep({
        ...currentSettings,
        customPayTypes: nextCustomPayTypes,
        updatedAt: nowIso(),
      }),
    };
  });
  window.dispatchEvent(new Event(BACKEND_EVENTS.employees));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return savedPayType;
};
